import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import PdfViewer from './PdfViewer';

const PAGE_SIZE = 20;

const TABS = [
    { id: 'main', label: 'Main Abstracts', desc: 'Oral & Poster Paper' },
    { id: 'medical', label: 'Medical (MBBS)', desc: 'Completed Undergrad' },
    { id: 'undergrad', label: 'Undergrad Students', desc: 'Student Submissions' },
    { id: 'settings', label: 'Settings', desc: 'Deadline & config' },
];

const Pagination = ({ currentPage, totalPages, onPageChange, totalItems, pageSize }) => {
    const from = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const to = Math.min(currentPage * pageSize, totalItems);

    const getPages = () => {
        const delta = 2;
        const pages = [];
        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) pages.push(i);
        }
        const items = [];
        let prev = null;
        for (const page of pages) {
            if (prev && page - prev > 1) items.push('...');
            items.push(page);
            prev = page;
        }
        return items;
    };

    return (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-4 py-3 border-t border-gray-100 bg-white">
            <p className="text-xs text-gray-500">
                Showing <span className="font-semibold text-gray-700">{from}–{to}</span> of <span className="font-semibold text-gray-700">{totalItems}</span>
                {totalPages > 1 && <span className="text-gray-400 ml-1">· Page {currentPage} of {totalPages}</span>}
            </p>
            {totalPages > 1 && (
                <div className="flex items-center gap-1">
                    <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}
                        className="px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                        ‹ Prev
                    </button>
                    {getPages().map((item, i) =>
                        item === '...'
                            ? <span key={`e${i}`} className="w-8 text-center text-xs text-gray-400">…</span>
                            : <button key={item} onClick={() => onPageChange(item)}
                                className={`w-8 h-8 text-xs font-semibold rounded-md transition-colors ${currentPage === item ? 'bg-indigo-600 text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                                {item}
                            </button>
                    )}
                    <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}
                        className="px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                        Next ›
                    </button>
                </div>
            )}
        </div>
    );
};

const SearchBar = ({ value, onChange, placeholder }) => (
    <div className="relative">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z" />
        </svg>
        <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
            className="w-full pl-8 pr-8 py-1.5 border border-gray-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent bg-white" />
        {value && (
            <button onClick={() => onChange('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-base leading-none">×</button>
        )}
    </div>
);

const CustomEventAbstractAdmin = () => {
    const [authenticated, setAuthenticated] = useState(false);
    const [credentials, setCredentials] = useState({ username: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [previewDoc, setPreviewDoc] = useState(null);
    const [resendingId, setResendingId] = useState(null);

    const [activeTab, setActiveTab] = useState('main');

    // Main abstracts
    const [mainData, setMainData] = useState([]);
    const [submissionType, setSubmissionType] = useState('oral-paper');
    const [categoryType, setCategoryType] = useState('consultant');
    const [mainSearch, setMainSearch] = useState('');
    const [mainPage, setMainPage] = useState(1);

    // Medical abstracts
    const [medicalData, setMedicalData] = useState([]);
    const [medicalSearch, setMedicalSearch] = useState('');
    const [medicalPage, setMedicalPage] = useState(1);

    // Undergrad abstracts
    const [undergradData, setUndergradData] = useState([]);
    const [undergradSearch, setUndergradSearch] = useState('');
    const [undergradPage, setUndergradPage] = useState(1);

    // Deadline settings
    const [deadlineEnabled, setDeadlineEnabled] = useState(false);
    const [deadlineValue, setDeadlineValue] = useState(''); // datetime-local string
    const [deadlineSaving, setDeadlineSaving] = useState(false);
    const [deadlineMsg, setDeadlineMsg] = useState('');

    useEffect(() => {
        const storedAuth = localStorage.getItem('kapconAdminAuth');
        if (storedAuth) {
            try {
                const parsed = JSON.parse(storedAuth);
                setCredentials({ username: parsed.username, password: parsed.password });
                setAuthenticated(true);
            } catch (e) { console.error(e); }
        }
    }, []);

    useEffect(() => {
        if (authenticated) {
            if (activeTab === 'main') fetchMain();
            else if (activeTab === 'medical') fetchMedical();
            else if (activeTab === 'undergrad') fetchUndergrad();
            else if (activeTab === 'settings') fetchDeadline();
        }
    }, [authenticated, activeTab, submissionType, categoryType]);

    // Reset page on search or filter change
    useEffect(() => { setMainPage(1); }, [mainSearch, submissionType, categoryType]);
    useEffect(() => { setMedicalPage(1); }, [medicalSearch]);
    useEffect(() => { setUndergradPage(1); }, [undergradSearch]);

    const handleLoginChange = (e) => setCredentials(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleLogin = async (e) => {
        e.preventDefault();
        setMessage('');
        setLoading(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/admin/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(credentials)
            });
            const data = await res.json();
            if (data.success) {
                localStorage.setItem('kapconAdminAuth', JSON.stringify({ username: credentials.username, password: credentials.password, role: data.role }));
                setAuthenticated(true);
            } else {
                setMessage(data.message || 'Invalid credentials');
                localStorage.removeItem('kapconAdminAuth');
            }
        } catch { setMessage('Error connecting to server'); }
        finally { setLoading(false); }
    };

    const handleLogout = () => {
        setAuthenticated(false);
        setCredentials({ username: '', password: '' });
        setMainData([]); setMedicalData([]); setUndergradData([]);
        localStorage.removeItem('kapconAdminAuth');
    };

    const authHeaders = { 'Content-Type': 'application/json' };
    const authBody = () => JSON.stringify(credentials);

    const handleAuthError = (res) => {
        if (res.status === 401) { setAuthenticated(false); localStorage.removeItem('kapconAdminAuth'); }
    };

    const fetchMain = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/admin/abstract/main`, {
                method: 'POST', headers: authHeaders,
                body: JSON.stringify({ ...credentials, submissionType, categoryType })
            });
            const data = await res.json();
            if (data.success) setMainData(data.data || []);
            else { handleAuthError(res); setMessage(data.message || 'Error fetching data'); }
        } catch { setMessage('Error connecting to server'); }
        finally { setLoading(false); }
    };

    const fetchMedical = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/admin/abstract/medical`, {
                method: 'POST', headers: authHeaders, body: authBody()
            });
            const data = await res.json();
            if (data.success) setMedicalData(data.data || []);
            else { handleAuthError(res); setMessage(data.message || 'Error fetching data'); }
        } catch { setMessage('Error connecting to server'); }
        finally { setLoading(false); }
    };

    const fetchUndergrad = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/admin/abstract/undergrad`, {
                method: 'POST', headers: authHeaders, body: authBody()
            });
            const data = await res.json();
            if (data.success) setUndergradData(data.data || []);
            else { handleAuthError(res); setMessage(data.message || 'Error fetching data'); }
        } catch { setMessage('Error connecting to server'); }
        finally { setLoading(false); }
    };

    const fetchDeadline = async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/abstract-deadline`);
            const data = await res.json();
            if (data.success) {
                setDeadlineEnabled(data.data.enabled || false);
                if (data.data.deadline) {
                    // Convert to datetime-local format (YYYY-MM-DDTHH:mm)
                    const d = new Date(data.data.deadline);
                    const pad = n => String(n).padStart(2, '0');
                    setDeadlineValue(`${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
                } else {
                    setDeadlineValue('');
                }
            }
        } catch { }
    };

    const saveDeadline = async () => {
        setDeadlineSaving(true);
        setDeadlineMsg('');
        try {
            // Convert the datetime-local string (local time) to a UTC ISO string
            // so the server always receives and stores the correct UTC time.
            const deadlineISO = deadlineValue ? new Date(deadlineValue).toISOString() : null;
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/admin/abstract-deadline`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ ...credentials, deadline: deadlineISO, enabled: deadlineEnabled }),
            });
            const data = await res.json();
            if (data.success) {
                setDeadlineMsg('✓ Deadline saved successfully');
                setTimeout(() => setDeadlineMsg(''), 3000);
            } else {
                setDeadlineMsg('Error: ' + (data.message || 'Failed to save'));
            }
        } catch {
            setDeadlineMsg('Error connecting to server');
        } finally {
            setDeadlineSaving(false);
        }
    };

    const refreshCurrent = () => {
        if (activeTab === 'main') fetchMain();
        else if (activeTab === 'medical') fetchMedical();
        else if (activeTab === 'undergrad') fetchUndergrad();
    };

    // Filtered data per tab
    const filteredMain = mainData.filter(p => {
        const q = mainSearch.toLowerCase();
        return !q || (p.confId || '').toLowerCase().includes(q) || (p.fullName || '').toLowerCase().includes(q) ||
            (p.email || '').toLowerCase().includes(q) || (p.organization || '').toLowerCase().includes(q) ||
            (p.membershipNum || '').toLowerCase().includes(q);
    });

    const filteredMedical = medicalData.filter(p => {
        const q = medicalSearch.toLowerCase();
        return !q || (p.confId || '').toLowerCase().includes(q) || (p.fullName || '').toLowerCase().includes(q) ||
            (p.email || '').toLowerCase().includes(q) || (p.institute || '').toLowerCase().includes(q) ||
            (p.contactNumber || '').includes(medicalSearch);
    });

    const filteredUndergrad = undergradData.filter(p => {
        const q = undergradSearch.toLowerCase();
        return !q || (p.name || '').toLowerCase().includes(q) || (p.college || '').toLowerCase().includes(q) ||
            (p.guideName || '').toLowerCase().includes(q) || (p.guideCollege || '').toLowerCase().includes(q) ||
            (p.guideConfId || '').toLowerCase().includes(q) || (p.state || '').toLowerCase().includes(q);
    });

    // Paginated slices
    const pagedMain = filteredMain.slice((mainPage - 1) * PAGE_SIZE, mainPage * PAGE_SIZE);
    const pagedMedical = filteredMedical.slice((medicalPage - 1) * PAGE_SIZE, medicalPage * PAGE_SIZE);
    const pagedUndergrad = filteredUndergrad.slice((undergradPage - 1) * PAGE_SIZE, undergradPage * PAGE_SIZE);

    const currentRawCount = activeTab === 'main' ? mainData.length : activeTab === 'medical' ? medicalData.length : undergradData.length;
    const currentFilteredCount = activeTab === 'main' ? filteredMain.length : activeTab === 'medical' ? filteredMedical.length : filteredUndergrad.length;

    const exportToExcel = () => {
        let exportData = [], sheetName = 'Abstracts', fileName = 'KAPCON_2026_Abstracts.xlsx';
        if (activeTab === 'main') {
            sheetName = 'Main Abstracts'; fileName = `KAPCON_2026_${submissionType}_${categoryType}.xlsx`;
            exportData = filteredMain.map(p => ({ 'Date': new Date(p.createdAt).toLocaleString(), 'Conf ID': p.confId, 'Name': p.fullName, 'Email': p.email, 'Phone': p.contactNumber, 'Organization': p.organization || '', 'State': p.state || '', 'Membership No.': p.membershipNum, 'Submission Type': p.submissionType, 'Category': p.categoryType, 'Abstract Doc URL': p.abstractDoc }));
        } else if (activeTab === 'medical') {
            sheetName = 'Medical Abstracts'; fileName = 'KAPCON_2026_Medical_Abstracts.xlsx';
            exportData = filteredMedical.map(p => ({ 'Date': new Date(p.createdAt).toLocaleString(), 'Conf ID': p.confId, 'Name': p.fullName, 'Email': p.email, 'Phone': p.contactNumber, 'Designation': p.designation || '', 'Institute': p.institute || '', 'State': p.state || '', 'Submission Type': p.submissionType, 'Abstract Doc URL': p.abstractDoc }));
        } else {
            sheetName = 'Undergrad Abstracts'; fileName = 'KAPCON_2026_Undergrad_Abstracts.xlsx';
            exportData = filteredUndergrad.map(p => ({ 'Date': new Date(p.createdAt).toLocaleString(), 'Student Name': p.name, 'College': p.college, 'State': p.state, 'Guide Name': p.guideName, 'Guide Designation': p.guideDesignation, 'Guide College': p.guideCollege, 'Guide State': p.guideState, 'Guide Conf ID': p.guideConfId, 'Abstract Doc URL': p.abstractDoc }));
        }
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
        XLSX.writeFile(wb, fileName);
    };

    const handleResendEmail = async (id) => {
        setResendingId(id);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/admin/abstract/main/resend-email/${id}`, {
                method: 'POST', headers: authHeaders, body: authBody()
            });
            const data = await res.json();
            alert(data.message || (data.success ? 'Email sent!' : 'Failed to send email'));
        } catch {
            alert('Error connecting to server');
        } finally {
            setResendingId(null);
        }
    };

    const ViewBtn = ({ doc }) => doc
        ? <button onClick={() => setPreviewDoc(doc)} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
            View
        </button>
        : <span className="text-xs text-gray-300">—</span>;

    // ---------- LOGIN ----------
    if (!authenticated) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
                <div className="w-full max-w-sm bg-white rounded-2xl shadow-lg border border-gray-100 p-8 space-y-6">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-indigo-600 mb-4">
                            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        </div>
                        <h2 className="text-xl font-bold text-gray-900">Abstract Admin</h2>
                        <p className="text-sm text-gray-500 mt-1">KAPCON 2026 — Sign in to continue</p>
                    </div>
                    <form className="space-y-4" onSubmit={handleLogin}>
                        <input name="username" type="text" required value={credentials.username} onChange={handleLoginChange}
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Username" />
                        <input name="password" type="password" required value={credentials.password} onChange={handleLoginChange}
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Password" />
                        {message && <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg font-medium">{message}</p>}
                        <button type="submit" disabled={loading}
                            className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 text-white text-sm font-semibold rounded-lg transition-colors">
                            {loading ? 'Signing in...' : 'Sign In'}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    // ---------- ADMIN PANEL ----------
    return (
        <div className="min-h-screen bg-gray-50">

            {/* Top Bar */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
                    <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-md bg-indigo-600 flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        </div>
                        <span className="font-bold text-gray-900 text-sm">Abstract Admin</span>
                        <span className="text-gray-300 text-xs hidden sm:block">| KAPCON 2026</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={refreshCurrent}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                            Refresh
                        </button>
                        <button onClick={handleLogout} className="px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors">Logout</button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-4">

                {/* Tab Navigation */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="grid grid-cols-3 divide-x divide-gray-100">
                        {TABS.map(tab => (
                            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                                className={`py-3 px-4 text-left transition-all ${activeTab === tab.id ? 'bg-indigo-50 border-b-2 border-indigo-600' : 'hover:bg-gray-50 border-b-2 border-transparent'}`}>
                                <div className={`text-sm font-bold ${activeTab === tab.id ? 'text-indigo-700' : 'text-gray-700'}`}>{tab.label}</div>
                                <div className="text-xs text-gray-400 mt-0.5">{tab.desc}</div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Main Abstract Filters */}
                {activeTab === 'main' && (
                    <div className="grid grid-cols-2 gap-3">
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Submission Type</p>
                            <div className="flex bg-gray-100 rounded-lg p-0.5 gap-0.5">
                                {['oral-paper', 'poster-paper'].map(t => (
                                    <button key={t} onClick={() => setSubmissionType(t)}
                                        className={`flex-1 py-1.5 text-xs font-bold capitalize rounded-md transition-all ${submissionType === t ? 'bg-white shadow text-indigo-700' : 'text-gray-500 hover:text-gray-700'}`}>
                                        {t.replace('-', ' ')}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Category</p>
                            <div className="flex bg-gray-100 rounded-lg p-0.5 gap-0.5">
                                {['consultant', 'postgraduate'].map(t => (
                                    <button key={t} onClick={() => setCategoryType(t)}
                                        className={`flex-1 py-1.5 text-xs font-bold capitalize rounded-md transition-all ${categoryType === t ? 'bg-white shadow text-indigo-700' : 'text-gray-500 hover:text-gray-700'}`}>
                                        {t}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Stats + Search + Export Bar */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3">
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex items-center gap-1.5">
                            <span className="text-xs text-gray-500 font-medium">Total</span>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-100 text-indigo-700">{currentRawCount}</span>
                            {currentFilteredCount !== currentRawCount && (
                                <span className="text-xs text-gray-400">→ {currentFilteredCount} matching</span>
                            )}
                            {loading && <span className="text-xs text-gray-400 animate-pulse ml-1">Loading...</span>}
                        </div>
                        {/* Per-tab search */}
                        {activeTab === 'main' && (
                            <SearchBar value={mainSearch} onChange={setMainSearch} placeholder="Search conf ID, name, email, membership..." />
                        )}
                        {activeTab === 'medical' && (
                            <SearchBar value={medicalSearch} onChange={setMedicalSearch} placeholder="Search conf ID, name, email, institute..." />
                        )}
                        {activeTab === 'undergrad' && (
                            <SearchBar value={undergradSearch} onChange={setUndergradSearch} placeholder="Search student, college, guide, conf ID..." />
                        )}
                    </div>
                    <button onClick={exportToExcel}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-md transition-colors shadow-sm shrink-0">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                        Export Excel
                    </button>
                </div>

                {/* ---- MAIN ABSTRACTS TABLE ---- */}
                {activeTab === 'main' && (
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200">
                                        <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider w-24">Date</th>
                                        <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Conf ID</th>
                                        <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Participant</th>
                                        <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Membership No.</th>
                                        <th className="px-4 py-3 text-center text-[10px] font-bold text-gray-500 uppercase tracking-wider w-24">Doc</th>
                                        <th className="px-4 py-3 text-center text-[10px] font-bold text-gray-500 uppercase tracking-wider w-28">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {loading && pagedMain.length === 0 ? (
                                        <tr><td colSpan="6" className="px-4 py-10 text-center text-sm text-gray-400">Loading...</td></tr>
                                    ) : pagedMain.length === 0 ? (
                                        <tr><td colSpan="6" className="px-4 py-10 text-center text-sm text-gray-400">{mainSearch ? 'No results match your search.' : 'No submissions found.'}</td></tr>
                                    ) : pagedMain.map(p => (
                                        <tr key={p._id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="text-xs text-gray-800 font-medium">{new Date(p.createdAt).toLocaleDateString('en-GB')}</div>
                                                <div className="text-[10px] text-gray-400">{new Date(p.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className="font-mono text-xs font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">{p.confId}</span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="text-sm font-semibold text-gray-900">{p.fullName}</div>
                                                <div className="text-xs text-gray-500 mt-0.5">{p.email}</div>
                                                <div className="text-xs text-gray-400">{p.organization}{p.state ? `, ${p.state}` : ''}</div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className="font-mono text-xs text-gray-700">{p.membershipNum || '—'}</span>
                                            </td>
                                            <td className="px-4 py-3 text-center"><ViewBtn doc={p.abstractDoc} /></td>
                                            <td className="px-4 py-3 text-center">
                                                <button
                                                    onClick={() => handleResendEmail(p._id)}
                                                    disabled={resendingId === p._id}
                                                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors">
                                                    {resendingId === p._id ? 'Sending...' : 'Resend Mail'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination currentPage={mainPage} totalPages={Math.ceil(filteredMain.length / PAGE_SIZE)} onPageChange={setMainPage} totalItems={filteredMain.length} pageSize={PAGE_SIZE} />
                    </div>
                )}

                {/* ---- MEDICAL ABSTRACTS TABLE ---- */}
                {activeTab === 'medical' && (
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200">
                                        <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider w-24">Date</th>
                                        <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Conf ID</th>
                                        <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Participant</th>
                                        <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Designation / Institute</th>
                                        <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider w-28">Type</th>
                                        <th className="px-4 py-3 text-center text-[10px] font-bold text-gray-500 uppercase tracking-wider w-24">Doc</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {loading && pagedMedical.length === 0 ? (
                                        <tr><td colSpan="6" className="px-4 py-10 text-center text-sm text-gray-400">Loading...</td></tr>
                                    ) : pagedMedical.length === 0 ? (
                                        <tr><td colSpan="6" className="px-4 py-10 text-center text-sm text-gray-400">{medicalSearch ? 'No results match your search.' : 'No submissions found.'}</td></tr>
                                    ) : pagedMedical.map(p => (
                                        <tr key={p._id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="text-xs text-gray-800 font-medium">{new Date(p.createdAt).toLocaleDateString('en-GB')}</div>
                                                <div className="text-[10px] text-gray-400">{new Date(p.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className="font-mono text-xs font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">{p.confId}</span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="text-sm font-semibold text-gray-900">{p.fullName}</div>
                                                <div className="text-xs text-gray-500 mt-0.5">{p.email}</div>
                                                <div className="text-xs text-gray-400">{p.contactNumber}{p.state ? ` · ${p.state}` : ''}</div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="text-xs font-semibold text-gray-700">{p.designation || '—'}</div>
                                                <div className="text-xs text-gray-400 mt-0.5">{p.institute || '—'}</div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded-full uppercase tracking-wide ${p.submissionType === 'oral-paper' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                                                    {p.submissionType === 'oral-paper' ? 'Oral Paper' : p.submissionType}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-center"><ViewBtn doc={p.abstractDoc} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination currentPage={medicalPage} totalPages={Math.ceil(filteredMedical.length / PAGE_SIZE)} onPageChange={setMedicalPage} totalItems={filteredMedical.length} pageSize={PAGE_SIZE} />
                    </div>
                )}

                {/* ---- UNDERGRAD ABSTRACTS TABLE ---- */}
                {activeTab === 'undergrad' && (
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200">
                                        <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider w-24">Date</th>
                                        <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Student</th>
                                        <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Guide Details</th>
                                        <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Guide Conf ID</th>
                                        <th className="px-4 py-3 text-center text-[10px] font-bold text-gray-500 uppercase tracking-wider w-24">Doc</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {loading && pagedUndergrad.length === 0 ? (
                                        <tr><td colSpan="5" className="px-4 py-10 text-center text-sm text-gray-400">Loading...</td></tr>
                                    ) : pagedUndergrad.length === 0 ? (
                                        <tr><td colSpan="5" className="px-4 py-10 text-center text-sm text-gray-400">{undergradSearch ? 'No results match your search.' : 'No submissions found.'}</td></tr>
                                    ) : pagedUndergrad.map(p => (
                                        <tr key={p._id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="text-xs text-gray-800 font-medium">{new Date(p.createdAt).toLocaleDateString('en-GB')}</div>
                                                <div className="text-[10px] text-gray-400">{new Date(p.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="text-sm font-semibold text-gray-900">{p.name}</div>
                                                <div className="text-xs text-gray-500 mt-0.5">{p.college}</div>
                                                <div className="text-xs text-gray-400">{p.state}</div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="text-xs font-semibold text-gray-800">{p.guideName}</div>
                                                <div className="text-xs text-gray-500 mt-0.5">{p.guideDesignation}</div>
                                                <div className="text-xs text-gray-400">{p.guideCollege}, {p.guideState}</div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className="font-mono text-xs font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">{p.guideConfId}</span>
                                            </td>
                                            <td className="px-4 py-3 text-center"><ViewBtn doc={p.abstractDoc} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination currentPage={undergradPage} totalPages={Math.ceil(filteredUndergrad.length / PAGE_SIZE)} onPageChange={setUndergradPage} totalItems={filteredUndergrad.length} pageSize={PAGE_SIZE} />
                    </div>
                )}

                {/* ---- SETTINGS TAB ---- */}
                {activeTab === 'settings' && (
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 max-w-lg">
                        <h3 className="text-base font-bold text-gray-900 mb-1">Abstract Submission Deadline</h3>
                        <p className="text-xs text-gray-500 mb-5">
                            Once enabled and the deadline passes, all public abstract submission forms will show a "submissions closed" message automatically.
                        </p>

                        {/* Enable / Disable toggle */}
                        <div className="flex items-center justify-between mb-5 p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <div>
                                <p className="text-sm font-semibold text-gray-800">Enable deadline enforcement</p>
                                <p className="text-xs text-gray-500 mt-0.5">Forms will close after the date &amp; time below</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setDeadlineEnabled(v => !v)}
                                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none ${deadlineEnabled ? 'bg-indigo-600' : 'bg-gray-300'}`}
                            >
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${deadlineEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                            </button>
                        </div>

                        {/* Date + time picker */}
                        <div className="mb-5">
                            <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">
                                Deadline Date &amp; Time
                            </label>
                            <input
                                type="datetime-local"
                                value={deadlineValue}
                                onChange={e => setDeadlineValue(e.target.value)}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
                            />
                            <p className="text-xs text-gray-400 mt-1">Time is in your local timezone</p>
                        </div>

                        {/* Status preview */}
                        {deadlineValue && (
                            <div className={`mb-5 px-4 py-3 rounded-lg border text-sm font-medium flex items-center gap-2 ${
                                deadlineEnabled && new Date() > new Date(deadlineValue)
                                    ? 'bg-red-50 border-red-200 text-red-700'
                                    : deadlineEnabled
                                        ? 'bg-green-50 border-green-200 text-green-700'
                                        : 'bg-gray-50 border-gray-200 text-gray-600'
                            }`}>
                                <span className={`w-2 h-2 rounded-full shrink-0 ${
                                    deadlineEnabled && new Date() > new Date(deadlineValue) ? 'bg-red-500' :
                                    deadlineEnabled ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
                                }`} />
                                {!deadlineEnabled
                                    ? 'Deadline enforcement is off — forms are open'
                                    : new Date() > new Date(deadlineValue)
                                        ? `Forms are CLOSED — deadline passed on ${new Date(deadlineValue).toLocaleString()}`
                                        : `Forms are OPEN — will close on ${new Date(deadlineValue).toLocaleString()}`
                                }
                            </div>
                        )}

                        {/* Save button */}
                        <button
                            type="button"
                            onClick={saveDeadline}
                            disabled={deadlineSaving}
                            className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 text-white text-sm font-bold rounded-lg transition-colors"
                        >
                            {deadlineSaving ? 'Saving…' : 'Save Deadline Settings'}
                        </button>

                        {deadlineMsg && (
                            <p className={`mt-3 text-sm text-center font-medium ${deadlineMsg.startsWith('✓') ? 'text-green-600' : 'text-red-600'}`}>
                                {deadlineMsg}
                            </p>
                        )}
                    </div>
                )}

            </div>

            {/* Document Preview Modal */}
            {previewDoc && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden">
                        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                            <span className="text-sm font-bold text-gray-800">Document Preview</span>
                            <div className="flex items-center gap-4">
                                <a href={previewDoc} target="_blank" rel="noreferrer" className="text-xs font-semibold text-indigo-600 hover:text-indigo-800">Open in Tab ↗</a>
                                <button onClick={() => setPreviewDoc(null)} className="text-gray-400 hover:text-red-500 text-xl font-bold leading-none">&times;</button>
                            </div>
                        </div>
                        <div className="flex-1 bg-gray-100 relative">
                            {previewDoc.toLowerCase().endsWith('.pdf')
                                ? <PdfViewer url={previewDoc} />
                                : <iframe src={`https://docs.google.com/gview?url=${encodeURIComponent(previewDoc)}&embedded=true`} title="Document Preview" className="w-full h-full border-0 absolute inset-0" />
                            }
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomEventAbstractAdmin;
